import type { BareFetcher } from "swr";
import useSWR from "swr";
import type { PublicConfiguration } from "swr/_internal";
import { AppError } from "@/models/appError";
import {
  convertContestResponseToIContestDetailOrUndefined,
  type IContestDetail,
} from "@/models/contestDetail";
import { QueryRepository } from "@/repositories/queryRepository";
import { useAuth } from "@/hooks/useAuth";

type SWRError = AppError;

const REQUEST_ID = "fetch-contest";

type Data = IContestDetail;

const fetcher = async (contestId: string): Promise<IContestDetail> => {
  const queryRepository = new QueryRepository();

  const response = await queryRepository.quickQuery([
    {
      requestId: REQUEST_ID,
      rawQueryRequest: {
        contestRequest: {
          id: { value: contestId },
          requireContest: {
            requireId: { value: true },
            requireStatus: { value: true },
            requireName: { value: true },
            requireThumbnail: {
              requireServingUrl: { value: true },
            },
            requireLegalAgreement: {
              requireId: { value: true },
              requireName: { value: true },
              requireLatestVersion: {
                requireVersionId: { value: true },
                requireDescriptionHtml: { value: true },
                requireUserAccepted: { value: true },
              },
            },
            requireUserEntrySeriesList: {
              requireId: { value: true },
            },
          },
        },
      },
    },
  ]);

  const contestPage = response.responseList.find(
    (item) => item.requestId?.value === REQUEST_ID,
  )?.contest;

  if (!contestPage) {
    throw new Error("Contest not found");
  }

  const contestDetail =
    convertContestResponseToIContestDetailOrUndefined(contestPage);
  if (!contestDetail) {
    throw new Error("Contest detail not found");
  }

  return contestDetail;
};

type Params = {
  contestId: string | undefined;
  config?: Partial<PublicConfiguration<Data, SWRError, BareFetcher<Data>>>;
};

export const useFetchContestDetail = ({ contestId, config }: Params) => {
  const { user } = useAuth();
  return useSWR<IContestDetail, SWRError>(
    user && contestId ? [REQUEST_ID, contestId] : undefined,
    ([_, _contestId]: [string, string]) => fetcher(_contestId),
    config,
  );
};
